import { ESRI, GIS } from '@rsos/maps/constants/reverseGeocodeServices';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import gisReverseGeocodeLocation from './gisReverseGeocodeLocation';
import { MapsNotReadyError } from './googleGeocoderReady';
import googleReverseGeocodeLocation from './googleReverseGeocodeLocation';

/**
 * Esri world geocode enhanced service url is used when a premium authority does not have a local
 * gis geocode service configured.
 * https://developers.arcgis.com/documentation/mapping-apis-and-services/service-endpoints/#geocoding-service-1
 */
export const ESRI_GEOCODE_SERVICE_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/';

/** Address attributes returned from the GIS or Esri reverse geocode service. */
export interface GISAddressCandidate {
  Addr_type?: string;
  LongLabel?: string;
  Match_addr?: string;
  Address?: string;
  Street?: string;
  City?: string;
  Region?: string;
  Postal?: string;
  CntryName?: string;
}

/** Normalized address object shared across reverse geocode providers. */
export interface ReverseGeocodeAddressObject {
  home: string;
  street: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}

export interface ReverseGeocodeResult {
  address: string;
  addressObject: ReverseGeocodeAddressObject | null;
  rgAddressProvider?: string;
}

export interface ReverseGeocodeLocationArgs {
  latitude: number;
  longitude: number;
  onReverseGeocoding?: (isReverseGeocoding: boolean) => void;
  service?: string;
  serviceUrl?: string | null;
  token?: string | null;
}

/**
 * Parse the Esri address candidate and return the formatted address and addressObject. If the
 * address type is a Point of Interest (poi) such as businesses, landmarks, or geographic features,
 * use the address long label so that the Point of Interest name is included in the reverse geocode
 * address (rgAddress) field.
 * https://developers.arcgis.com/rest/geocode/api-reference/geocoding-service-output.htm
 * @param addressCandidate - The address attributes returned from GIS or Esri reverse geocode service.
 * @param rgAddressProvider - The reverse geocoding service address provider.
 */
export const parseGISAddressCandidate = (
  addressCandidate?: GISAddressCandidate,
  rgAddressProvider?: string,
): ReverseGeocodeResult => {
  const address =
    addressCandidate?.['Addr_type']?.toLowerCase() === 'poi'
      ? (addressCandidate?.['LongLabel'] ?? '')
      : addressCandidate?.['Match_addr']
        ? addressCandidate['Match_addr']
        : '';

  const addressObject: ReverseGeocodeAddressObject = {
    home: addressCandidate?.['Address'] || '',
    street: addressCandidate?.['Street'] || '',
    city: addressCandidate?.['City'] || '',
    region: addressCandidate?.['Region'] || '',
    postal_code: addressCandidate?.['Postal'] || '',
    country: addressCandidate?.['CntryName'] || '',
  };

  return { address, addressObject, rgAddressProvider };
};

/**
 * Parse the Google address components and return a formatted address object.
 * @param addressComponents - The address components returned from Google reverse geocode service.
 */
export const parseGoogleAddressComponents = (
  addressComponents?: google.maps.GeocoderAddressComponent[] | null,
): ReverseGeocodeAddressObject => {
  const ShouldBeComponent: Record<keyof ReverseGeocodeAddressObject, string[]> =
    {
      home: ['street_number'],
      street: ['street_address', 'route'],
      city: [
        'locality',
        'sublocality',
        'sublocality_level_1',
        'sublocality_level_2',
        'sublocality_level_3',
        'sublocality_level_4',
      ],
      region: [
        'administrative_area_level_1',
        'administrative_area_level_2',
        'administrative_area_level_3',
        'administrative_area_level_4',
        'administrative_area_level_5',
      ],
      postal_code: ['postal_code'],
      country: ['country'],
    };

  const address: ReverseGeocodeAddressObject = {
    home: '',
    street: '',
    city: '',
    region: '',
    postal_code: '',
    country: '',
  };

  if (addressComponents) {
    addressComponents.forEach(component => {
      (
        Object.keys(ShouldBeComponent) as (keyof ReverseGeocodeAddressObject)[]
      ).forEach(shouldBe => {
        if (ShouldBeComponent[shouldBe].indexOf(component.types[0]) !== -1) {
          address[shouldBe] =
            shouldBe === 'country' ? component.short_name : component.long_name;
        }
      });
    });
  }

  return address;
};

/**
 * Reverse geocode coordinates to an address. Returns a formatted address and addressObject. The
 * reverse geocode service priority should be GIS, then Esri, then Google depending on if an address
 * is found.
 */
export const reverseGeocodeLocationToAddress = async ({
  latitude,
  longitude,
  onReverseGeocoding,
  service,
  serviceUrl,
  token,
}: ReverseGeocodeLocationArgs): Promise<ReverseGeocodeResult | null> => {
  switch (service) {
    case GIS: {
      try {
        if (!serviceUrl || !token)
          throw new Error('Missing GIS service url or token.');

        const addressCandidate = await gisReverseGeocodeLocation({
          latitude,
          longitude,
          onReverseGeocoding,
          serviceUrl,
          token,
        });

        return addressCandidate
          ? parseGISAddressCandidate(addressCandidate, 'GIS')
          : null;
      } catch {
        return reverseGeocodeLocationToAddress({
          latitude,
          longitude,
          onReverseGeocoding,
          service: ESRI,
          serviceUrl: ESRI_GEOCODE_SERVICE_URL,
        });
      }
    }
    case ESRI: {
      try {
        const addressCandidate = await gisReverseGeocodeLocation({
          latitude,
          longitude,
          onReverseGeocoding,
          serviceUrl: ESRI_GEOCODE_SERVICE_URL,
        });

        return addressCandidate
          ? parseGISAddressCandidate(addressCandidate, 'Esri')
          : null;
      } catch {
        return reverseGeocodeLocationToAddress({
          latitude,
          longitude,
          onReverseGeocoding,
        });
      }
    }
    default: {
      try {
        const { address, addressObject } = await googleReverseGeocodeLocation({
          latitude,
          longitude,
          onReverseGeocoding,
        });

        return {
          address,
          addressObject: addressObject
            ? parseGoogleAddressComponents(addressObject)
            : null,
          rgAddressProvider: 'Google',
        };
      } catch (error) {
        if (error instanceof MapsNotReadyError) {
          // Expected: the Google Maps Geocoder only loads once the user opens
          // the Google map, so a reverse geocode requested before then has no
          // Geocoder to use. This is not a failure, so log at warn level (which
          // Sentry's console integration does not capture) rather than
          // console.error; it resolves naturally once the map is open.
          // eslint-disable-next-line no-console
          console.warn(error.message);
        } else {
          // A genuine reverse geocode failure. Report it with location context
          // and a stable fingerprint so real problems are clear and grouped
          // on their own rather than buried in transient "not ready" noise.
          captureExceptionWithScope(
            error instanceof Error ? error : new Error(String(error)),
            { latitude, longitude, service: 'Google' },
            ['reverse-geocode', 'google'],
          );
        }
        return null;
      }
    }
  }
};
